/**
 * use-session-launch.ts — entering a live session from the portal, written
 * once: ask the backend for the session's launch URL, open the game in a new
 * tab in the language the portal is showing (withUiLanguage), and keep which
 * session is launching and why one failed. The sessions page's cards and a
 * gameplay activity's page both enter through it; each wrote the flow out.
 */
import { useCallback, useState } from 'react';
import { useLaunchSession } from '../../api/student/sessions/hooks';
import { withUiLanguage } from '../../utils/launch-url';

/** The way into a live session: enter one by its id, the one being entered, and why the last try failed. */
export function useSessionLaunch() {
  const { mutateAsync } = useLaunchSession();
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enter = useCallback(
    async (sessionId: string) => {
      setLaunchingId(sessionId);
      setError(null);
      try {
        const { launchUrl } = await mutateAsync(sessionId);
        window.open(withUiLanguage(launchUrl), '_blank', 'noopener,noreferrer');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLaunchingId(null);
      }
    },
    [mutateAsync],
  );

  return { enter, launchingId, error };
}
